import { useCallback, useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Button,
  Group,
  Text,
  Loader,
  Center,
  Alert,
  Box,
  Stack,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconArrowLeft,
  IconDownload,
  IconAlertCircle,
  IconExternalLink,
  IconFileText,
} from "@tabler/icons-react";
import { portalApiClient } from "../../lib/portalApiClient";
import {
  getResponseDownloadFilename,
  triggerBlobDownload,
} from "../../utils/downloadFilename";

export function PortalReportViewPage() {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadFileName, setDownloadFileName] = useState("report.pdf");
  const blobUrlRef = useRef<string | null>(null);
  const pdfBlobRef = useRef<Blob | null>(null);

  useEffect(() => {
    if (!caseId) return;

    let cancelled = false;
    let createdUrl: string | null = null;
    setLoading(true);
    setError(null);
    setPdfBlobUrl(null);
    pdfBlobRef.current = null;

    portalApiClient
      .get(`/dashboard/reports/${caseId}/pdf`, { responseType: "blob" })
      .then((res) => {
        if (cancelled) return;
        const pdfBlob = res.data as Blob;
        createdUrl = URL.createObjectURL(pdfBlob);
        blobUrlRef.current = createdUrl;
        pdfBlobRef.current = pdfBlob;
        setPdfBlobUrl(createdUrl);
        setDownloadFileName(
          getResponseDownloadFilename(
            res.headers["content-disposition"],
            "report.pdf"
          )
        );
      })
      .catch(() => {
        if (!cancelled) setError("Failed to load report. Please try again.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      pdfBlobRef.current = null;
      if (createdUrl) {
        URL.revokeObjectURL(createdUrl);
      }
      if (blobUrlRef.current === createdUrl) {
        blobUrlRef.current = null;
      }
    };
  }, [caseId]);

  const handleDownload = useCallback(() => {
    if (!pdfBlobRef.current) {
      setError("The report PDF is not ready to download. Please try again.");
      return;
    }
    triggerBlobDownload(pdfBlobRef.current, downloadFileName);
  }, [downloadFileName]);

  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 64px)",
        background: "#fafbff",
        fontFamily: "'IBM Plex Sans', sans-serif",
      }}
    >
      {/* Top bar */}
      <Box
        style={{
          borderBottom: "1px solid #e5e7f8",
          background: "#ffffff",
          padding: "12px 24px",
          flexShrink: 0,
          boxShadow: "0 1px 4px rgba(4,17,154,0.06)",
        }}
      >
        <Group justify="space-between" align="center">
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate("/portal/dashboard")}
            style={{
              fontFamily: "'IBM Plex Sans', sans-serif",
              color: "#374151",
              fontWeight: 500,
            }}
          >
            Back to Reports
          </Button>

          <Group gap="sm">
            {pdfBlobUrl && !isMobile && (
              <Button
                variant="outline"
                leftSection={<IconDownload size={16} />}
                onClick={handleDownload}
                color="gcpBlue"
                size="sm"
                style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
              >
                Download PDF
              </Button>
            )}
          </Group>
        </Group>
      </Box>

      {/* Content area */}
      <Box style={{ flex: 1, overflow: "hidden", position: "relative" }}>
        {loading && (
          <Center style={{ height: "100%" }}>
            <Stack align="center" gap="md">
              <Loader color="gcpBlue" />
              <Text
                size="sm"
                c="dimmed"
                style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
              >
                Loading report...
              </Text>
            </Stack>
          </Center>
        )}

        {!loading && error && (
          <Center style={{ height: "100%" }}>
            <Alert
              icon={<IconAlertCircle />}
              color="red"
              title="Could not load report"
              style={{
                maxWidth: 400,
                fontFamily: "'IBM Plex Sans', sans-serif",
              }}
            >
              {error}
            </Alert>
          </Center>
        )}

        {!loading &&
          pdfBlobUrl &&
          (isMobile ? (
            <Center style={{ height: "100%", padding: "2rem" }}>
              <Stack align="center" gap="lg">
                <Box
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    background: "rgba(4,17,154,0.06)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <IconFileText size={40} color="#04119A" />
                </Box>
                <Stack gap={4} style={{ textAlign: "center" }}>
                  <Text
                    style={{
                      fontFamily: "'DM Serif Display', serif",
                      fontSize: "1.3rem",
                      color: "#1e1e3f",
                    }}
                  >
                    Report Ready
                  </Text>
                  <Text
                    size="sm"
                    c="dimmed"
                    style={{
                      maxWidth: 260,
                      fontFamily: "'IBM Plex Sans', sans-serif",
                    }}
                  >
                    Open in your browser&apos;s PDF viewer for the best reading
                    experience.
                  </Text>
                </Stack>
                <Stack gap="sm" style={{ width: "100%", maxWidth: 280 }}>
                  <Button
                    fullWidth
                    size="md"
                    leftSection={<IconExternalLink size={16} />}
                    onClick={() =>
                      window.open(
                        `${pdfBlobUrl}#toolbar=0&navpanes=0`,
                        "_blank"
                      )
                    }
                    style={{
                      background:
                        "linear-gradient(135deg, #04119A 0%, #1a2cc4 100%)",
                      border: "none",
                      fontFamily: "'IBM Plex Sans', sans-serif",
                      fontWeight: 500,
                    }}
                  >
                    Open PDF
                  </Button>
                  <Button
                    fullWidth
                    variant="outline"
                    color="gcpBlue"
                    size="md"
                    leftSection={<IconDownload size={16} />}
                    onClick={handleDownload}
                    style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
                  >
                    Download PDF
                  </Button>
                </Stack>
              </Stack>
            </Center>
          ) : (
            <iframe
              src={`${pdfBlobUrl}#toolbar=0&navpanes=0`}
              title="Pathology Report"
              style={{ width: "100%", height: "100%", border: "none" }}
            />
          ))}
      </Box>
    </Box>
  );
}

export default PortalReportViewPage;
